import React from 'react'
import { Lock } from '@phosphor-icons/react'
import useNavigation, { JourneyStep } from 'src/lib/hooks/useNavigation'
import { classNames } from 'src/lib/utils/helpers'
import { useUser } from 'src/lib/contexts/UserContext'
import { useOrganisationContext } from 'src/lib/contexts/OrganisationContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { createJourneyConfig, ProgressStep } from 'src/lib/utils/journeyConfig'
import { JourneyNavigationComponent } from 'src/components/navigation/SecondaryNavBars/common/NavigationWrapper'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@shopflo/ui'
import { useRouter } from 'next/router'

const JourneyProgressTracker: React.FC = () => {
  const {
    getters: { journeyStep, sidebarOpen, isTablet, openHoverCardId },
    actions: { setJourneyStepAndNavigate, setOpenHoverCardId }
  } = useNavigation()

  const { canAccess, isUserConfigured, user } = useUser()
  const {
    getters: { billingPopUp }
  } = useOrganisationContext()
  const { t } = useLocale()

  const router = useRouter()

  const hasOverdueInvoices = billingPopUp.hasOverdueInvoices && !user?.isSuperAdmin
  const journeyConfig = createJourneyConfig(journeyStep, canAccess, isUserConfigured, hasOverdueInvoices)

  const hasPlgRedirectionFlow = (step: ProgressStep) => {
    return step?.plgRedirectionUrl && user?.isAdmin
  }

  const handleStepClick = (step: ProgressStep) => {
    if (hasPlgRedirectionFlow(step) && !step.isEnabled && step?.plgRedirectionUrl) {
      router.push(step.plgRedirectionUrl)
      return
    }
    if (isTablet) {
      setOpenHoverCardId(step.id)
      return
    }
    if (!step.isEnabled || isTablet) return
    setJourneyStepAndNavigate(step.id, user)
  }

  const getDisabledTooltipText = (step: ProgressStep) => {
    if (step.id === JourneyStep.CART) {
      return t('navigation.cart_disabled_tootip')
    }
    if (step.id === JourneyStep.PDP) {
      return t('navigation.pdp_disabled_tooltip')
    }
    if (step.id === JourneyStep.SHOP_PASS) {
      return t('navigation.shopass_disabled_tooltip')
    }
  }

  const renderStepIcon = (step: ProgressStep) => {
    const shadowColor = step.isEnabled || hasPlgRedirectionFlow(step) ? step.shadowColor : '#9CA3AF'
    return (
      <div
        className={classNames(
          'w-6 h-6 flex items-center justify-center rounded-md flex-shrink-0 text-white',
          step.isEnabled || hasPlgRedirectionFlow(step) ? step.color : 'bg-gray-400'
        )}
        style={{ boxShadow: `0 0 0 0.5px ${shadowColor} inset, 0 1.2px 0 0 rgba(255,255,255,0.80) inset` }}
      >
        <img src={step.imageUrl} alt={step.title} className='w-6 h-6 rounded-md' />
      </div>
    )
  }

  const renderStepTitle = (step: ProgressStep) => {
    if (!sidebarOpen) return null

    return (
      <div className='flex items-start'>
        <span
          className={classNames(
            'text-text-sm-medium transition-colors duration-200 text-zinc-600 justify-start text-nowrap',
            !step.isEnabled && !hasPlgRedirectionFlow(step) ? 'opacity-40' : ''
          )}
        >
          {step.title}
        </span>
        {!step.isEnabled && !hasPlgRedirectionFlow(step) && (
          <div className='flex justify-center items-center h-full'>
            <Icon IconComponent={Lock} size='xxs' className='mt-1 ml-2 text-gray-400' />
          </div>
        )}
      </div>
    )
  }

  const getButtonStyles = (step: ProgressStep) => {
    const baseClasses = 'relative flex w-full gap-2 items-start rounded-md p-1.5'
    const activeClasses = step.isActive
      ? 'bg-white rounded-xl shadow-[0_0_0_1.5px_rgba(54,65,83,0.20),0_0.5px_1px_0_rgba(54,65,83,0.20)]'
      : ''
    const disabledClasses = !step.isEnabled && !hasPlgRedirectionFlow(step) ? 'cursor-not-allowed' : ''
    const hoverClasses =
      (!step.isEnabled && !hasPlgRedirectionFlow(step)) || isTablet ? '' : 'hover:bg-black/5 transition-colors'

    return classNames(baseClasses, activeClasses, disabledClasses, hoverClasses)
  }

  const renderStepButton = (step: ProgressStep) => (
    <button onClick={() => handleStepClick(step)} className={getButtonStyles(step)}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className='flex gap-2 items-start w-fit'>
            {renderStepIcon(step)}
            {renderStepTitle(step)}
          </div>
        </TooltipTrigger>
        {!step.isEnabled && !hasPlgRedirectionFlow(step) && (
          <TooltipContent side='right'>
            <p className='text-text-xs-medium'>{getDisabledTooltipText(step)}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </button>
  )

  const renderStep = (step: ProgressStep) => {
    const isTabletMode = isTablet && step.isEnabled

    if (isTabletMode) {
      return (
        <div key={step.id} className='relative' id={step.buttonId}>
          <HoverCard
            open={openHoverCardId === step.id}
            onOpenChange={(open: boolean) => setOpenHoverCardId(open ? step.id : null)}
          >
            <HoverCardTrigger asChild>
              <div>{renderStepButton(step)}</div>
            </HoverCardTrigger>
            <HoverCardContent
              side='right'
              align='center'
              sideOffset={8}
              className='p-0 m-0 ml-1 rounded-xl border-0 shadow-none'
            >
              <div className='w-64 bg-gray-100 rounded-xl h-fit shrink-0 border-black/6 border-1'>
                <JourneyNavigationComponent stepId={step.id} />
              </div>
            </HoverCardContent>
          </HoverCard>
        </div>
      )
    }

    return (
      <div key={step.id} className='relative' id={step.buttonId}>
        {renderStepButton(step)}
      </div>
    )
  }

  return (
    <TooltipProvider>
      <div className={classNames('flex flex-col p-2 w-full', !sidebarOpen ? 'items-start' : '')}>
        {sidebarOpen ? (
          <div className='text-text-xs-medium py-1 px-1.5 text-zinc-500 text-nowrap'>{t('navigation.journey')}</div>
        ) : (
          <div className='mb-4 w-full h-px bg-gray-200' />
        )}

        <div className='relative'>
          <div className='absolute left-[18px] top-4 bottom-4 w-px bg-gray-200' />

          <div className='items-start'>{journeyConfig.map((step, index) => renderStep(step))}</div>
        </div>
      </div>
    </TooltipProvider>
  )
}

export default JourneyProgressTracker
